import { useEffect, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './lib/supabase'
import { LoginScreen } from './screens/LoginScreen'
import { ForgotPasswordScreen } from './screens/ForgotPasswordScreen'
import { SetPasswordScreen } from './screens/SetPasswordScreen'
import { VillageListScreen } from './screens/VillageListScreen'
import { subscribeLang } from './lib/i18n'

/**
 * Four states, not two: signed out, signed in, asking for a reset link, and
 * arriving from one.
 *
 * The session is read once at startup and then kept in step with an auth
 * listener. Reading it once would be a bug — a token refresh, a sign-out on
 * another tab, or an expired session would leave the UI claiming a login that
 * no longer exists.
 */
export function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [checking, setChecking] = useState(true)
  const [forgot, setForgot] = useState(false)

  // THE RECOVERY CASE. Opening a reset link signs the person in immediately, so
  // without this they would land on the village list with their old password
  // still set, and be locked out again tomorrow — reasonably concluding that
  // the reset "did not work". The flag is what forces them through choosing a
  // new one before anything else.
  const [recovering, setRecovering] = useState(false)

  // Redraw everything when the language changes. t() is a plain function, so
  // without this only the component holding the switch would update.
  const [, redraw] = useState(0)
  useEffect(() => subscribeLang(() => redraw((n) => n + 1)), [])

  useEffect(() => {
    let cancelled = false

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return
      setSession(data.session)
      setChecking(false)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next)
      if (event === 'PASSWORD_RECOVERY') {
        setRecovering(true)
        setForgot(false)
      }
      // USER_UPDATED after a recovery means the new password is saved and the
      // person can get on with their day.
      if (event === 'USER_UPDATED') setRecovering(false)
      if (event === 'SIGNED_OUT') {
        setRecovering(false)
        setForgot(false)
      }
    })

    // Supabase fires PASSWORD_RECOVERY as the link is processed, which can
    // happen before this listener is attached. The URL fragment is the
    // durable evidence, so it is checked directly as well.
    if (/type=recovery/.test(window.location.hash)) setRecovering(true)

    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
  }, [])

  // Without this, a signed-in user sees the login screen flash on every launch
  // — which in the field reads as "it logged me out again".
  if (checking) {
    return (
      <div className="screen">
        <div className="center-card">
          <p className="spinner">Checking your session…</p>
        </div>
      </div>
    )
  }

  if (recovering && session) {
    return <SetPasswordScreen email={session.user.email ?? null} />
  }

  if (!session) {
    return forgot ? (
      <ForgotPasswordScreen onBack={() => setForgot(false)} />
    ) : (
      <LoginScreen onForgot={() => setForgot(true)} />
    )
  }

  return <VillageListScreen session={session} />
}
